import { DOCUMENT } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { SlugifyPipe } from 'ngx-pipes';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { IdName } from '@app-services/oem-dealer.service';
import { MatDialog } from '@angular/material/dialog';
import { DialogPersonalInfoComponent } from '@app-components/evaluation/evaluation-vehicle-detail/dialog/dialog-personal-info/dialog-personal-info.component';
import { AdminService } from '@app/views/private/admin/services/admin.service';
import { NavigationService } from '@app-services/navigation.service';
import { SessionService } from 'src/app/store/session/session.service';
import { OemDealers, Permission, UserInfo } from 'src/app/store/session/session.store';
import { StompService } from '@app-services/stomp.service';
import { reloadCurrentRoute as reloadRoute } from '@app-utils/router-utils';

type MenuTab = 'OEM_DEALER' | 'DEALER';

interface QuickLink { label: string; path: string; }

@Component({
  selector: 'app-header-account-dropdown',
  templateUrl: './header-account-dropdown.component.html',
  styleUrls: ['./header-account-dropdown.component.scss'],
})
export class HeaderAccountDropdownComponent implements OnInit, OnChanges, OnDestroy {
  private readonly router          = inject(Router);
  private readonly sessionService  = inject(SessionService);
  private readonly dialog          = inject(MatDialog);
  private readonly adminService    = inject(AdminService);
  private readonly stompService    = inject(StompService);
  private readonly navService      = inject(NavigationService);
  private readonly document        = inject(DOCUMENT);
  private readonly destroyRef      = inject(DestroyRef);
  private readonly elementRef      = inject(ElementRef<HTMLElement>);

  @Input() userInfo: UserInfo;

  isOpen       = false;
  isClosing    = false;
  hasInteracted = false;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  optionSelected: MenuTab = 'OEM_DEALER';

  filteredOemDealers: OemDealers[] = [];
  filteredDealers: IdName[]        = [];
  search = '';

  private readonly slugify = new SlugifyPipe();
  quickLinks: QuickLink[] = [];

  copiedIndex: number | null  = null;
  hidingIndex: number | null  = null;
  private copyTimer: ReturnType<typeof setTimeout> | null = null;
  private copyHideTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.initLists();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userInfo']) {
      this.initLists();
    }
  }

  ngOnDestroy(): void {
    if (this.closeTimer)    clearTimeout(this.closeTimer);
    if (this.copyTimer)     clearTimeout(this.copyTimer);
    if (this.copyHideTimer) clearTimeout(this.copyHideTimer);
  }

  private initLists(): void {
    if (!this.userInfo) return;

    if (this.hasDealerGroup() && !this.hasOemDealerGroup()) {
      this.optionSelected = 'DEALER';
    } else {
      this.optionSelected = 'OEM_DEALER';
    }

    if (this.userInfo.oemDealers && this.userInfo.oemDealers.length > 1) {
      this.filteredOemDealers = this.userInfo.oemDealers.filter(o => o.inactive === false);
    } else {
      this.filteredOemDealers = this.userInfo.oemDealers ?? [];
    }

    this.filteredDealers = this.userInfo.dealers ?? [];

    this.search = '';

    const slug = this.slugify.transform(this.userInfo.name ?? '');
    const id   = this.userInfo.id;
    const quickLinks: QuickLink[] = [];

    if (this.userInfo.hasPermission(Permission.PROVIDE_LINK_PLUGIN_RAIOX)) {
      quickLinks.push({
        label: 'Plugin Raiox Online',
        path: `/plugin-raiox/seller/${slug}/${id}`,
      });
    }

    if (this.userInfo.hasPermission(Permission.PROVIDE_LINK_RAIOX_ONLINE)) {
      quickLinks.push({
        label: 'Avaliação Online',
        path: `/online-eval/inicio/vendedor/${slug}/${id}`,
      });
    }

    this.quickLinks = quickLinks;
  }

  hasOemDealerGroup(): boolean {
    return !!(this.userInfo?.oemDealers?.length);
  }

  hasDealerGroup(): boolean {
    return !!(this.userInfo?.dealers?.length);
  }

  get primaryNameDisplay(): string {
    if (!this.userInfo) return '';
    return this.userInfo.oemDealerGroupName ?? `Olá, ${this.userInfo.firstName}`;
  }

  get secondaryNameDisplay(): string | null {
    const u = this.userInfo;
    if (!u) return null;
    if (u.oemDealerGroupName && u.oemDealers?.length) {
      return u.oemDealerGroupSelected ? u.oemDealerGroupSelected.name : 'Visão consolidada';
    }
    if (u.oemDealerGroupName) return u.oemDealerGroupName;
    if (u.oemDealerName)      return u.oemDealerName;
    if (u.dealerName)         return u.dealerName;
    return null;
  }

  get hasSecondaryName(): boolean { return this.secondaryNameDisplay !== null; }

  get displayedMenuTitle(): string {
    return this.secondaryNameDisplay ?? this.primaryNameDisplay;
  }

  get displayedOrganizationName(): string | null {
    const u = this.userInfo;
    if (!u) return null;
    const sec = this.secondaryNameDisplay;
    if (u.dealerName    && u.dealerName    !== sec) return u.dealerName;
    if (u.oemDealerName && u.oemDealerName !== sec) return u.oemDealerName;
    return null;
  }

  isDealer(): boolean { return !!(this.userInfo?.hasRole('DEALER')); }

  isOemDealerSelected(oemDealer: OemDealers): boolean {
    return !!(this.userInfo?.oemDealerGroupSelected &&
              this.userInfo.oemDealerGroupSelected.id === oemDealer.id);
  }

  isDealerSelected(dealer: IdName): boolean {
    return !!(this.userInfo?.dealerGroupSelected &&
              this.userInfo.dealerGroupSelected.id === dealer.id);
  }

  filterDealers(query: string): void {
    if (this.optionSelected === 'OEM_DEALER') {
      if (query) {
        this.filteredOemDealers = (this.userInfo.oemDealers ?? [])
          .filter(s => s.name.toLowerCase().includes(query.toLowerCase()));
      } else {
        if (this.userInfo.oemDealers && this.userInfo.oemDealers.length > 1) {
          this.filteredOemDealers = this.userInfo.oemDealers.filter(o => o.inactive === false);
        } else {
          this.filteredOemDealers = this.userInfo.oemDealers ?? [];
        }
      }
    } else {
      if (query) {
        this.filteredDealers = (this.userInfo.dealers ?? [])
          .filter(s => s.name.toLowerCase().includes(query.toLowerCase()));
      } else {
        this.filteredDealers = this.userInfo.dealers ?? [];
      }
    }
  }

  selectTab(tab: MenuTab): void {
    this.optionSelected = tab;
    this.search = '';
    this.filterDealers('');
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.isOpen) return;
    const target = event.target as Node | null;
    if (target && !this.elementRef.nativeElement.contains(target)) {
      this.closeDropdown();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void { this.closeDropdown(); }

  toggleDropdown(): void {
    this.hasInteracted = true;
    if (this.isOpen) { this.closeDropdown(); return; }
    if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null; }
    this.isOpen    = true;
    this.isClosing = false;
  }

  closeDropdown(_immediate = false): void {
    if (!this.isOpen) return;
    if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null; }
    this.isOpen    = false;
    this.isClosing = false;
  }

  onClickOemDealers(oemDealer: OemDealers | null): void {
    this.sessionService.updateOemDealersSelected(oemDealer);
    this.reloadCurrentRoute();
  }

  onClickDealers(dealer: IdName): void {
    this.stompService.disconnect();
    this.sessionService.updateDealersSelected(dealer);
    this.navService.getFastQuoteCount();
    this.reloadCurrentRoute();
  }

  openPersonalInfo(): void {
    if (!this.userInfo?.dealerId) return;
    this.closeDropdown();
    this.adminService.getDealer(this.userInfo.dealerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(resp => {
        this.dialog.open(DialogPersonalInfoComponent, {
          width: '780px', hasBackdrop: true, data: { dealer: resp },
        }).afterClosed()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(success => { if (success) this.document.location?.reload(); });
      });
  }

  copyQuickLink(link: QuickLink, index: number): void {
    if (!navigator?.clipboard?.writeText) return;
    const origin = (this.document.defaultView ?? window).location.origin;
    const url = `${origin}${link.path}`;
    void navigator.clipboard.writeText(url).then(() => {
      if (this.copyTimer)     clearTimeout(this.copyTimer);
      if (this.copyHideTimer) clearTimeout(this.copyHideTimer);
      this.copiedIndex = index;
      this.hidingIndex = null;
      this.copyTimer = setTimeout(() => {
        this.hidingIndex = index;
        this.copyHideTimer = setTimeout(() => {
          this.copiedIndex = null;
          this.hidingIndex = null;
        }, 200);
      }, 1800);
    });
  }

  trackByIndex(index: number): number { return index; }

  private reloadCurrentRoute(): void {
    this.closeDropdown(true);
    void reloadRoute(this.router);
  }
}
